import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isWithinBounds,
  useGetFootprintingBeefHerdDetails,
  useMutateFootprintingBeefHerdDetails,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

type BeefHerdDetailsFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const BeefHerdDetailsForm = ({ primaryDataId, readOnly, farmId, year }: BeefHerdDetailsFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [avgCowLosses, setAvgCowLosses] = useState<number | null>(null);
  const [avgCowUsefulLifeMonths, setAvgCowUsefulLifeMonths] = useState<number | null>(null);

  const { data: herdDetails, isSuccess, isLoading } = useGetFootprintingBeefHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });

  const { mutate: mutateHerdDetails } = useMutateFootprintingBeefHerdDetails(primaryDataId ?? "", farmId, year, {
    avgCowLosses,
    avgCowUsefulLifeMonths,
  });

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setAvgCowLosses(herdDetails.avgCowLosses ?? null);
      setAvgCowUsefulLifeMonths(herdDetails.avgCowUsefulLifeMonths ?? null);

      formRef.current?.setFieldsValue({
        avgCowLosses: herdDetails.avgCowLosses ?? null,
        avgCowUsefulLifeMonths: herdDetails.avgCowUsefulLifeMonths ?? null,
      });

      const fieldsToValidate = (["avgCowLosses", "avgCowUsefulLifeMonths"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef}>
      <Flex vertical gap={16}>
        <Card border dataTestId="footprinting-beef-herd-details-form" className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("beefHerdDetailsForm.cowLostQuestion")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("beefHerdDetailsForm.cowLostQuestionDescription")}
              </Typography>
            </Flex>
            <InputNumber
              disabled={readOnly}
              name="avgCowLosses"
              dataTestId="footprinting-beef-herd-cow-losses-input"
              label={t("beefHerdDetailsForm.question.avgCowLossesInput")}
              suffix={t("beefHerdDetailsForm.question.avgCowLossesSuffix")}
              value={avgCowLosses}
              initialValue={avgCowLosses}
              onChange={(v) => setAvgCowLosses(v)}
              validateFirst
              rules={[
                { required: true, type: "number", message: t("beefHerdDetailsForm.error.validNumberCowLoss") },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("beefHerdDetailsForm.error.validNumberCowLoss")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>

        <Card border className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("beefHerdDetailsForm.avgCowUsefulLifeMonthsQuestion")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("beefHerdDetailsForm.avgCowUsefulLifeMonthsQuestionDescription")}
              </Typography>
            </Flex>
            <InputNumber
              disabled={readOnly}
              name="avgCowUsefulLifeMonths"
              dataTestId="footprinting-beef-herd-cow-useful-life-input"
              label={t("beefHerdDetailsForm.question.avgCowUsefulLifeMonthsInput")}
              suffix={t("beefHerdDetailsForm.question.avgCowUsefulLifeMonthsSuffix")}
              value={avgCowUsefulLifeMonths}
              initialValue={avgCowUsefulLifeMonths}
              onChange={(v) => setAvgCowUsefulLifeMonths(v)}
              validateFirst
              rules={[
                { required: true, type: "number", message: t("beefHerdDetailsForm.error.validNumberMonths") },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isWithinBounds(v, 20, 150)) {
                      return Promise.reject(new Error(t("beefHerdDetailsForm.error.validNumberMonths")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>
      </Flex>
    </Form>
  );
};

BeefHerdDetailsForm.Key = "beef.herdDetails";
export { BeefHerdDetailsForm };
